import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  BookOpen, Users, ClipboardList, MessageCircle, BarChart2,
  Award, Heart, ChevronRight, Star, ArrowRight,
  Atom, Stethoscope, Cpu, Activity, GraduationCap, CheckCircle2
} from "lucide-react";
import EnquiryForm from "../components/EnquiryForm";
import Gallery from "../components/Gallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RESULTS_IMG = "https://customer-assets.emergentagent.com/job_exam-excellence-hub/artifacts/1bm2ddrx_ChatGPT%20Image%20Mar%205%2C%202026%2C%2002_53_33%20AM.png";

const DEFAULT_HERO_IMG = "https://customer-assets.emergentagent.com/job_exam-excellence-hub/artifacts/t5remked_4.jpeg";
const DEFAULT_STATS = [
  { value: "1000+", label: "Students Mentored" },
  { value: "50+", label: "Top Rankers" },
  { value: "5+", label: "Years of Excellence" },
  { value: "3", label: "Expert Directors" },
];

// dynamic stats loaded from API

const features = [
  { icon: <GraduationCap size={22} color="#0A2540" />, title: "Expert Faculty", desc: "Highly experienced directors specialized in Chemistry, Mathematics & Biology." },
  { icon: <Atom size={22} color="#0A2540" />, title: "Concept-Based Learning", desc: "Focus on deep conceptual clarity, not rote learning." },
  { icon: <ClipboardList size={22} color="#0A2540" />, title: "Regular Testing", desc: "Frequent tests and mock exams to track student progress." },
  { icon: <MessageCircle size={22} color="#0A2540" />, title: "Doubt Solving", desc: "Dedicated sessions for clearing all doubts with personal attention." },
  { icon: <BookOpen size={22} color="#0A2540" />, title: "Study Material", desc: "Comprehensive, updated material aligned with board & entrance patterns." },
  { icon: <BarChart2 size={22} color="#0A2540" />, title: "Performance Tracking", desc: "Regular assessments with detailed feedback for improvement." },
  { icon: <Users size={22} color="#0A2540" />, title: "Small Batches", desc: "Limited batch sizes ensure personalized attention for every student." },
  { icon: <Heart size={22} color="#0A2540" />, title: "Parent Communication", desc: "Regular updates to parents about student performance and progress." },
];

const courses = [
  {
    icon: <BookOpen size={28} color="#F2C94C" />,
    title: "11th Science",
    desc: "Build a strong foundation in the critical first year of science stream.",
    highlights: ["PCM & PCB", "Board exam focus", "Concept clarity"],
  },
  {
    icon: <GraduationCap size={28} color="#F2C94C" />,
    title: "12th Science",
    desc: "Board prep + entrance exam integration for maximum results.",
    highlights: ["HSC board prep", "Entrance oriented", "Mock test series"],
  },
  {
    icon: <Stethoscope size={28} color="#F2C94C" />,
    title: "NEET Preparation",
    desc: "Medical entrance exam coaching with Biology, Physics & Chemistry.",
    highlights: ["NCERT based", "Previous papers", "High success rate"],
  },
  {
    icon: <Cpu size={28} color="#F2C94C" />,
    title: "JEE Main & Advanced",
    desc: "Engineering entrance coaching with deep problem-solving approach.",
    highlights: ["JEE Main + Advanced", "Numerical practice", "Expert guidance"],
  },
  {
    icon: <Activity size={28} color="#F2C94C" />,
    title: "MHT-CET Preparation",
    desc: "Maharashtra state entrance exam for engineering & pharmacy.",
    highlights: ["PCM & PCB groups", "State board aligned", "CET mock tests"],
  },
];

const steps = [
  { step: "01", title: "Concept Explanation", desc: "Expert faculty explain concepts with clarity and real-world examples." },
  { step: "02", title: "Practice Problems", desc: "Extensive practice with solved examples and unsolved exercises." },
  { step: "03", title: "Doubt Clearing", desc: "Dedicated doubt sessions so no concept remains unclear." },
  { step: "04", title: "Weekly Tests", desc: "Regular evaluations to assess understanding and identify gaps." },
  { step: "05", title: "Mock Exams", desc: "Full-length mock exams before final exams to boost confidence." },
];

const toppers_static = [];
const testimonials_static = [];

export default function Home() {
  const [toppers, setToppers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [content, setContent] = useState({ hero_title: "Where Dreams", hero_title_gold: "Meet Reality", hero_subtitle: "Premier science coaching for 11th & 12th HSC, NEET, JEE, and MHT-CET.", hero_image: DEFAULT_HERO_IMG, stats: DEFAULT_STATS });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/toppers`).then((r) => setToppers(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/testimonials`).then((r) => setTestimonials(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/content`).then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const stats = content.stats?.length ? content.stats : DEFAULT_STATS;
  const heroImg = content.hero_image || DEFAULT_HERO_IMG;
  const displayToppers = toppers.length > 0 ? toppers : [];
  const displayTestimonials = testimonials.length > 0 ? testimonials : [];

  return (
    <div data-testid="home-page" className="pt-16 md:pt-20">
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0A2540 0%, #0d3362 60%, #0A2540 100%)" }}
      >
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5" style={{ background: "#F2C94C", transform: "translate(30%, -30%)" }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5" style={{ background: "#F2C94C", transform: "translate(-30%, 30%)" }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="text-white animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-6">
                <Award size={14} color="#F2C94C" />
                <span className="text-xs font-semibold tracking-wide text-yellow-300">
                  Kandivali East, Mumbai | Est. 2020
                </span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Where Dreams
                <span className="block" style={{ color: "#F2C94C" }}>Meet Reality</span>
              </h1>
              <p className="text-base md:text-lg text-blue-100 leading-relaxed mb-8 max-w-lg">
                Premier science coaching for 11th & 12th HSC, NEET, JEE, and MHT-CET.
                Expert faculty, concept-based learning, and proven results.
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                <Link
                  to="/courses"
                  data-testid="hero-explore-courses-btn"
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-sm transition-all hover:opacity-90 active:scale-95"
                  style={{ backgroundColor: "#F2C94C", color: "#0A2540" }}
                >
                  Explore Courses <ChevronRight size={16} />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-enquire-btn"
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-sm border-2 border-white/40 text-white hover:bg-white/10 transition-all"
                >
                  Enquire Now <ArrowRight size={16} />
                </Link>
              </div>

              {/* Mini stats */}
              <div className="flex flex-wrap gap-6">
                {["NEET", "JEE", "MHT-CET", "HSC Board"].map((exam) => (
                  <div key={exam} className="flex items-center gap-2">
                    <CheckCircle2 size={16} color="#F2C94C" />
                    <span className="text-sm text-blue-200 font-medium">{exam}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image */}
            <div className="animate-fade-in-up-delay-2 hidden lg:block">
              <div className="relative">
                <div
                  className="absolute -inset-2 rounded-2xl opacity-30"
                  style={{ background: "linear-gradient(135deg, #F2C94C, transparent)" }}
                />
                <img
                  src={heroImg}
                  alt="Students at Trident Academy"
                  className="relative rounded-2xl w-full object-cover shadow-2xl"
                  style={{ height: "420px" }}
                />
                {/* Floating result card */}
                <div
                  className="absolute -bottom-5 -left-5 bg-white rounded-xl px-5 py-3 shadow-xl border-l-4"
                  style={{ borderLeftColor: "#F2C94C" }}
                >
                  <div className="text-2xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>1000+</div>
                  <div className="text-xs text-gray-500 font-semibold">Students Mentored</div>
                </div>
                <div
                  className="absolute -top-5 -right-5 bg-white rounded-xl px-5 py-3 shadow-xl border-l-4"
                  style={{ borderLeftColor: "#F2C94C" }}
                >
                  <div className="text-2xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>50+</div>
                  <div className="text-xs text-gray-500 font-semibold">Top Rankers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section data-testid="stats-section" style={{ backgroundColor: "#0A2540" }} className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center" data-testid={`stat-item-${i}`}>
                <div className="stat-number">{s.value}</div>
                <div className="text-gray-300 text-sm mt-1 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-choose-section" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>
              Our Advantage
            </p>
            <h2
              className="text-3xl md:text-5xl font-extrabold"
              style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
            >
              Why Choose <span style={{ color: "#F2C94C" }}>Trident Academy?</span>
            </h2>
            <div className="section-divider mt-4"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 border border-gray-100 card-hover"
                data-testid={`feature-card-${i}`}
              >
                <div className="feature-icon mb-4">{f.icon}</div>
                <h3 className="font-bold text-base mb-2" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
                  {f.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES OVERVIEW */}
      <section data-testid="courses-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>
              Programs Offered
            </p>
            <h2
              className="text-3xl md:text-5xl font-extrabold"
              style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
            >
              Our Courses
            </h2>
            <div className="section-divider mt-4"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, i) => (
              <div
                key={i}
                className="course-card rounded-2xl p-6 bg-white"
                data-testid={`course-card-${i}`}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: "#0A2540" }}
                >
                  {course.icon}
                </div>
                <h3
                  className="text-xl font-bold mb-2"
                  style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                >
                  {course.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{course.desc}</p>
                <ul className="space-y-1.5 mb-5">
                  {course.highlights.map((h, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 size={14} color="#F2C94C" />
                      {h}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/courses"
                  className="inline-flex items-center gap-1 text-sm font-bold transition-colors hover:gap-2"
                  style={{ color: "#0A2540" }}
                  data-testid={`course-learn-more-${i}`}
                >
                  Learn More <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section
        data-testid="cta-banner-1"
        className="py-14"
        style={{ background: "linear-gradient(135deg, #0A2540, #0d3362)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2
              className="text-2xl md:text-3xl font-extrabold text-white mb-1"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Book a <span style={{ color: "#F2C94C" }}>FREE Counseling Session</span>
            </h2>
            <p className="text-blue-200 text-sm">
              Get personalized guidance from our expert faculty on the right course for you.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="cta-enquire-btn"
            className="flex-shrink-0 px-8 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: "#F2C94C", color: "#0A2540" }}
          >
            Enquire Now &rarr;
          </Link>
        </div>
      </section>

      {/* TEACHING METHODOLOGY */}
      <section data-testid="methodology-section" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>
              How We Teach
            </p>
            <h2
              className="text-3xl md:text-5xl font-extrabold"
              style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
            >
              Our Teaching Methodology
            </h2>
            <div className="section-divider mt-4"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 border border-gray-100 flex gap-4 card-hover"
                data-testid={`step-card-${i}`}
              >
                <div className="step-circle">{step.step}</div>
                <div>
                  <h3
                    className="font-bold text-base mb-2"
                    style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOPPERS MARQUEE */}
      <section data-testid="toppers-section" className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#F2C94C" }}>
                Our Pride
              </p>
              <h2
                className="text-3xl font-extrabold"
                style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
              >
                Our Toppers
              </h2>
            </div>
            <Link
              to="/results"
              className="text-sm font-bold flex items-center gap-1 hover:gap-2 transition-all"
              style={{ color: "#0A2540" }}
              data-testid="view-all-results-btn"
            >
              View All Results <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="marquee-container">
          <div className="animate-marquee gap-5" style={{ paddingLeft: "20px" }}>
            {[...toppers, ...toppers].map((t, i) => (
              <div key={i} className="topper-card mx-2.5">
                <div
                  className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: t.tag_color || "#0A2540" }}
                >
                  {t.initials}
                </div>
                <div className="font-bold text-sm" style={{ color: "#0A2540" }}>{t.name}</div>
                <div className="text-xs text-gray-500 mt-1">{t.score}</div>
                <div
                  className="text-xs font-semibold mt-1.5 px-2 py-0.5 rounded-full inline-block"
                  style={{ backgroundColor: "#FEF9C3", color: "#92400E" }}
                >
                  {t.exam}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESULTS IMAGE */}
      <section data-testid="results-image-section" className="py-10 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img
            src={RESULTS_IMG}
            alt="Trident Academy Results - Our Toppers"
            className="w-full rounded-2xl shadow-xl"
          />
        </div>
      </section>

      {/* GALLERY */}
      <Gallery />

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>
              Success Stories
            </p>
            <h2
              className="text-3xl md:text-5xl font-extrabold"
              style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
            >
              What Our Students Say
            </h2>
            <div className="section-divider mt-4"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="testimonial-card" data-testid={`testimonial-card-${i}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={14} fill="#F2C94C" color="#F2C94C" />
                  ))}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5 italic">"{t.quote}"</p>
                <div>
                  <div className="font-bold text-sm" style={{ color: "#0A2540" }}>{t.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{t.exam}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY SECTION */}
      <section
        data-testid="home-enquiry-section"
        className="py-16 md:py-24"
        style={{ backgroundColor: "#f8faff" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>
                Get In Touch
              </p>
              <h2
                className="text-3xl md:text-4xl font-extrabold mb-4"
                style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
              >
                Start Your Journey <span style={{ color: "#F2C94C" }}>Today</span>
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Fill out the form and our team will get back to you within 24 hours. 
                Get a free counseling session to find the right course for your goals.
              </p>
              <div className="space-y-4">
                {[
                  { icon: "🎯", text: "Personalized course guidance" },
                  { icon: "📅", text: "Flexible batch timings" },
                  { icon: "📞", text: "Call back within 24 hours" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-lg">{item.icon}</span>
                    <span className="text-sm text-gray-600 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Right */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
              <h3
                className="text-xl font-bold mb-6"
                style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
              >
                Send Us a Message
              </h3>
              <EnquiryForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
